import React, { useCallback, useMemo, type AnchorHTMLAttributes, type ReactNode, type Ref } from 'react';
import { useSelector } from 'react-redux';
import { RomaRouterProvider, type RenderRouterLink, type RouterOptions } from '@milanote/roma';
import { Link } from '../../node_module_clones/react-router';
import { getIsElectronSelector } from '../components/electron/electronSelector';
import { APP_URL } from '../../common/utils/urlConstants';

type RouterHistory = {
    getCurrentLocation: () => { search: string };
};

type RouterLinkProps = Omit<AnchorHTMLAttributes<HTMLAnchorElement>, 'href'> & {
    to: string;
    innerRef?: Ref<HTMLAnchorElement>;
};

const RouterLink = Link as React.ComponentType<RouterLinkProps>;

/**
 * Delegates internal links to React Router while preserving native link behaviours.
 * `openInBrowser` links leave the desktop app: Electron's window-open handler sends new-window links to the
 * system browser.
 */
const createRenderRouterLink = (isElectron: boolean): RenderRouterLink => {
    const renderRouterLink: RenderRouterLink = (props, _renderProps, routerOptions) => {
        if (!('href' in props)) return <span {...props} />;

        const { href, ref, ...linkProps } = props;
        const useNativeLink =
            typeof href !== 'string' || !href.startsWith('/') || href.startsWith('//') || props.download != null;

        if (useNativeLink) return <a {...linkProps} href={href} ref={ref} />;

        if (isElectron && routerOptions?.openInBrowser) {
            return <a {...linkProps} href={`${APP_URL}${href}`} target="_blank" rel="noopener noreferrer" ref={ref} />;
        }

        return <RouterLink {...linkProps} to={href} innerRef={ref as Ref<HTMLAnchorElement>} />;
    };

    return renderRouterLink;
};

/**
 * Connects router-neutral Roma links to the Milanote client router.
 */
export const RomaRouterBridge: React.FC<{ children: ReactNode; history: RouterHistory }> = ({ children, history }) => {
    const isElectron: boolean = useSelector(getIsElectronSelector);
    const renderLink = useMemo(() => createRenderRouterLink(isElectron), [isElectron]);
    const resolveHref = useCallback(
        (href: string, options?: RouterOptions) =>
            options?.keepParams ? `${href}${history.getCurrentLocation().search}` : href,
        [history],
    );

    return (
        <RomaRouterProvider renderLink={renderLink} resolveHref={resolveHref}>
            {children}
        </RomaRouterProvider>
    );
};
